import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@/environments/environment';
import { AuthService } from '@/app/services/auth.service';

/**
 * Canonical AI Assist icon (PrimeIcons). The single source of truth — reference
 * it everywhere ai-assist appears (buttons, widget, dialogs) so the icon stays
 * consistent. For sidebar menu items use `AI_ASSIST_MENU_ICON` (adds the
 * fixed-width `pi-fw` modifier for alignment with the other menu entries).
 */
export const AI_ASSIST_ICON = 'pi pi-microchip-ai';
export const AI_ASSIST_MENU_ICON = 'pi pi-fw pi-microchip-ai';

/** Error envelope from the AI Assist service: HTTP 400 + `{ code }` (overview §6). */
export interface AiAssistErrorBody {
    code: string;
    message?: string;
}

/** Body of `POST /health` — liveness, vendor reachability, DB, in-flight jobs. */
export interface AiAssistHealth {
    status: string;
    version: string;
    vendors_reachable: { grok: boolean; flux: boolean; recraft: boolean };
    db: { reachable: boolean };
    active_jobs: number;
}

// Stable vendor identifiers shared with the AI Assist service balance API.
export const VENDOR_FLUX = 'flux';
export const VENDOR_RECRAFT = 'recraft';
export const VENDOR_GROK = 'grok';

/**
 * Vendor billing consoles — where the operator tops up credits. Opened in a new
 * tab from the credit indicators. Recraft has no stable billing deep-link, so
 * we land on the app (profile → Manage subscription from there). Adjust here if
 * a vendor changes its console URL.
 */
export const VENDOR_DASHBOARD_URLS: Record<string, string> = {
    [VENDOR_FLUX]: 'https://dashboard.bfl.ai',
    [VENDOR_RECRAFT]: 'https://www.recraft.ai/',
    [VENDOR_GROK]: 'https://console.x.ai/team/default/billing',
};

/**
 * Per-vendor credit-balance health (mirrors the service's `BalanceStatus`):
 * - `ok`           — comfortably above the warning threshold
 * - `low`          — below the warning threshold; operator should top up
 * - `critical`     — below the block threshold; generations are refused
 * - `unknown`      — the balance fetch failed (service fails open — no block)
 * - `unconfigured` — no balance source wired (e.g. Grok in v1)
 */
export type VendorBalanceStatus = 'ok' | 'low' | 'critical' | 'unknown' | 'unconfigured';

/** One vendor's credit balance + derived status (`POST /vendors/balance`). */
export interface VendorBalance {
    vendor: string; // 'flux' | 'recraft' | 'grok'
    status: VendorBalanceStatus;
    /** Remaining credits in `unit`; null when unknown / unconfigured. */
    balance: number | null;
    unit: string; // 'credits' | 'usd'
    low_threshold: number | null;
    critical_threshold: number | null;
    detail?: string | null;
}

/** Body of `POST /vendors/balance` — per-vendor balances + roll-up flags. */
export interface VendorBalancesResponse {
    vendors: VendorBalance[];
    /** At least one configured vendor is `critical` (drives the red dot). */
    any_critical: boolean;
    /** At least one configured vendor is `low` (drives the amber dot). */
    any_low: boolean;
}

/**
 * Overall service status for the topbar indicator dot:
 * - `ok`        — reachable, DB up, all vendors reachable (green)
 * - `degraded`  — reachable but something is off (DB or a vendor down) (yellow)
 * - `down`      — the service did not answer (red)
 * - `unknown`   — not probed yet (grey)
 */
export type AiAssistStatus = 'ok' | 'degraded' | 'down' | 'unknown';

// ── Contract 5 §3: staging inputs and submitting a kit ──────────────────────

export type InboundContentType = 'image/png' | 'image/jpeg' | 'image/webp';

/** Where to PUT one staged file, and the URL to send back in its place. */
export interface InboundFileTarget {
    name: string;
    upload_url: string;
    url: string;
    content_type: InboundContentType;
}

export interface InboundPresignResponse {
    request_id: string;
    job_id: string;
    files: InboundFileTarget[];
    expires_in_seconds: number;
}

/** At most this many files per presign call (service-side limit). */
export const MAX_INBOUND_FILES = 32;

export type KitVertical = 'skill_games' | 'social_casino' | 'skins_marketplace';

export interface KitBrand {
    display_name: string;
    domain: string;
    vertical: KitVertical;
    positioning: string | null;
}

/**
 * The site template brand kit. The three reference lists are separate on
 * purpose — the pages flow refuses to run on an empty page-reference list, and
 * `BrandKit.references` holds **zero** page references, so feeding it in would
 * produce off-spec output silently.
 *
 * Each group takes base64 **or** URLs, never both; this client always stages
 * the bytes and sends URLs, which keeps `create` at kilobytes instead of the
 * 3-15 MB a whole kit weighs inline.
 */
export interface KitTemplate {
    template_id: string;
    version: string;
    style_descriptor: Record<string, unknown> | null;
    icon_reference_urls: string[];
    banner_reference_urls: string[];
    page_reference_urls: string[];
    og_base_plate_url: string | null;
}

export interface KitBrandingFlags {
    brand_icon: boolean;
    og: boolean;
    seo: boolean;
    /** Required when `og` is on and `seo` is off — Contract 2 rejects an og finalize without one. */
    tagline: string | null;
}

export interface SiteKitCreateRequest {
    job_id: string;
    realm_id: string;
    /** `start` refuses a short draft, so this must match the items actually posted. */
    expected_item_count: number;
    brand: KitBrand;
    template: KitTemplate;
    branding: KitBrandingFlags;
}

export interface SiteKitCreateResponse {
    job_id: string;
    state: 'draft';
    items_received: number;
    expected_item_count: number;
}

export interface SiteKitItemRequest {
    job_id: string;
    /** Client-owned and stable; `game_template_id` is the authoritative id. */
    item_key: string;
    game_template_id: string;
    game_kind: string;
    source_name: string;
    source_description: string;
    icon_plate_url: string;
    banner_plate_url: string;
    copy: boolean;
    art: boolean;
    pages: { count: number } | null;
}

export interface SiteKitItemResponse {
    job_id: string;
    item_key: string;
    items_received: number;
    expected_item_count: number;
    replaced: boolean;
}

export interface SiteKitStartResponse {
    job_id: string;
    status: JobStatus;
    estimated_ready_in_seconds: number | null;
    /** True on the resume path — `start` on a failed, cancelled or partially-successful job. */
    resumed: boolean;
}

/** One game slot of the kit result. A null slot is a step that produced nothing. */
export interface SiteKitGameResult {
    item_key: string;
    game_template_id: string;
    title: string | null;
    description: string | null;
    icon_url: string | null;
    banner_url: string | null;
    lettering_url: string | null;
    page_urls: string[];
}

export interface SiteKitReportRow {
    item_key: string;
    step: string;
    status: 'failed' | 'skipped';
    code: string;
    attempts: number;
    message: string;
}

export interface SiteKitResult {
    branding: {
        seo_description: string | null;
        brand_icon: { url: string; favicon_url: string } | null;
        og: { url: string } | null;
    };
    games: SiteKitGameResult[];
    /** Empty means a clean run; non-empty is partial success, which is resumable. */
    report: SiteKitReportRow[];
    cost: SiteKitCost;
    latency_ms: number;
}

// ── Contract 5 §9: site-kit cost estimate ───────────────────────────────────

/** Per-vendor cost. FLUX and Recraft credits are different units and are never summed. */
export interface SiteKitCost {
    flux_credits: number;
    recraft_credits: number;
    grok_usd: number;
}

/** One planned game, as counts — the estimate never sees a payload. */
export interface SiteKitEstimateGame {
    art: boolean;
    copy: boolean;
    /** `null` = no pages for this game. */
    pages: { count: number } | null;
}

export interface SiteKitEstimateRequest {
    /** Optional here: the plan screen asks this **before** the realm exists. */
    realm_id: string | null;
    branding: { brand_icon: boolean; og: boolean; seo: boolean };
    games: SiteKitEstimateGame[];
}

export interface SiteKitEstimate {
    request_id: string;
    images: number;
    steps: number;
    eta_seconds: number;
    point: SiteKitCost;
    /**
     * The ceiling, machine-readable rather than prose: moderation-refused
     * attempts are billed, so it is the **worst case** that gets compared
     * against the balance, not the happy path.
     */
    worst_case: SiteKitCost;
}

/** A single async job in the operator's widget (`POST /asset/jobs/mine`). */
export interface AiAssistJob {
    job_id: string;
    kind: string;
    status: 'pending' | 'completed' | 'failed' | 'cancelled';
    /**
     * Which site the job belongs to. Contract 5 §13: without it the panel
     * cannot rediscover a provisioning run after a tab close, which is the
     * whole point of the field.
     */
    realm_id?: string | null;
    /** Only `site_kit` reports progress. */
    progress?: JobProgress | null;
    /** Populated only for `failed` jobs — the §6 error code + message. */
    error?: AiAssistErrorBody | null;
    created_at: string;
    completed_at?: string | null;
}

/** Body of `POST /asset/jobs/mine`. */
export interface MyJobsResponse {
    jobs: AiAssistJob[];
    active: number;
}

/** Token accounting returned by the Contract 4 text helpers. */
export interface AiAssistTokensUsed {
    input: number;
    output: number;
    reasoning: number;
}

/** Body of `POST /text/refine` (Contract 4 §3). */
export interface RefineRequest {
    /** The text to rewrite / improve (1–4000 chars). */
    text: string;
    /** Plain-language instruction, e.g. "fix grammar, keep tone playful". */
    instruction: string;
    /** Optional hard cap on the refined length (1–4000). */
    maxLength?: number | null;
}

/** Response of `POST /text/refine`. */
export interface RefineResponse {
    request_id: string;
    refined: string;
    alternatives: string[];
    vendor: string;
    tokens_used: AiAssistTokensUsed;
    cost_usd: number;
    latency_ms: number;
}

/**
 * Approved brand values forwarded so the SEO copy stays consistent
 * (Contract 4 §2).
 */
export interface BrandingContext {
    displayName?: string | null;
    name?: string | null;
}

/**
 * Body of `POST /text/suggest-branding` (camelCase; mapped to wire on send).
 * AI drafts the SEO meta description only — names are operator-supplied, the
 * logo split is admin/manual, About/Disclaimer are category boilerplate.
 */
export interface SuggestBrandingRequest {
    brandName: string;
    domain: string;
    /** Tone hint derived from the platform category (`skill_games` / `social_casino`). */
    vertical?: string | null;
    /** Website-template StyleDescriptor — opaque; only a personality hint is used. */
    style?: Record<string, unknown> | null;
    context?: BrandingContext;
    operatorNote?: string | null;
}

/** Drafts payload of `POST /text/suggest-branding`. */
export interface BrandingDrafts {
    seo_description: string[];
}

/** Response of `POST /text/suggest-branding`. */
export interface SuggestBrandingResponse {
    request_id: string;
    drafts: BrandingDrafts;
    vendor: string;
    tokens_used: AiAssistTokensUsed;
    cost_usd: number;
    latency_ms: number;
}

// ---------------------------------------------------------------------------
// Async image jobs (Contracts 1 & 2) — `POST` returns 202 + job_id; the result
// is delivered by polling `POST /asset/jobs/{job_id}/status`. No callbacks.
// ---------------------------------------------------------------------------

export type JobStatus = 'pending' | 'completed' | 'failed' | 'cancelled';
export type AssetJobKind = 'base_art' | 'asset_finalize' | 'pages' | 'site_kit';

/** 202 body from the async `POST /asset/...` generate / finalize endpoints. */
export interface JobAccepted {
    request_id: string;
    job_id: string;
    status: JobStatus;
    estimated_ready_in_seconds: number | null;
}

/** Polling response — `result` shape depends on `kind` (cast accordingly). */
/**
 * How far a long job has got (Contract 5 §8). `done` / `total` count **pipeline
 * steps, not images** — one `pages` step is one unit however many pages it
 * renders — and `total` is known at dispatch. `null` for every kind but
 * `site_kit`.
 */
export interface JobProgress {
    done: number;
    total: number;
    current_item_key: string | null;
    current_step: string | null;
}

export interface JobStatusResponse {
    job_id: string;
    kind: AssetJobKind;
    status: JobStatus;
    progress?: JobProgress | null;
    result: Record<string, unknown> | null;
    error: AiAssistErrorBody | null;
}

export type BaseArtAssetType = 'icon' | 'banner' | 'brand_icon';
export type GenerateMode = 'manual' | 'auto';

/** The downscaled favicon rendition shipped with each `brand_icon` candidate (A2). */
export interface BaseArtFaviconVariant {
    url: string;
    width: number;
    height: number;
    format: string;
    size_bytes: number;
}

export interface BaseArtCandidate {
    url: string;
    seed: number | null;
    width: number;
    height: number;
    format: string;
    size_bytes: number;
    /** brand_icon only — same mark downscaled for the favicon; else null. */
    favicon: BaseArtFaviconVariant | null;
}

/** `result` payload of a completed `base_art` job (Contract 1). */
export interface BaseArtResult {
    asset_type: BaseArtAssetType;
    scope: {
        template_id: string;
        version: string;
        game_template_id: string | null;
        game_kind: string | null;
    };
    candidates: BaseArtCandidate[];
    composed_prompt: string;
    authored_prompt: string | null;
    mode: GenerateMode;
    vendor: string;
    latency_ms: number;
    cost_credits: number;
}

/** Body of `POST /asset/base/generate` (camelCase; mapped to wire on send). */
export interface BaseArtGenerateRequest {
    /** Client-generated UUID — idempotency key (re-send = same job). */
    jobId: string;
    /**
     * Realm scope. REQUIRED for `brand_icon` (realm-level, PBE-24). For game
     * plates (`icon`/`banner`) the art is realm-independent (PBE-22, keyed by
     * game-template × website-template) — omit it; the service authorizes those
     * on global-admin instead.
     */
    realmId?: string | null;
    /** Website-template id + style version. Manual until templates are wired in. */
    templateId: string;
    version: string;
    /** Text-free brand references (1–8) as public URLs. Provide URLs OR base64. */
    referenceImageUrls?: string[];
    /** Text-free brand references as inline base64 (e.g. read via getSiteTemplateFile). */
    referenceImagesB64?: string[];
    assetType: BaseArtAssetType;
    styleDescriptor?: Record<string, unknown> | null;
    gameTemplateId?: string | null;
    gameKind?: string | null;
    gameMeta?: { name: string; description?: string } | null;
    /** `manual` requires `prompt`; `auto` authors the prompt from style/meta. */
    mode?: GenerateMode;
    prompt?: string | null;
    /**
     * "Semi-auto": extra operator detail folded into the Grok-authored prompt in
     * `auto` mode (Grok composes game + style + this note). Ignored in `manual`.
     */
    operatorNote?: string | null;
    samples?: number;
}

export type FinalizeAssetType = 'icon' | 'banner' | 'og';

export interface FinalizeCandidate {
    url: string;
    seed: number | null;
    width: number;
    height: number;
    format: string;
    size_bytes: number;
    /**
     * icon/banner (v0.7): the transparent logo PNG behind this candidate. Reuse
     * it on the next format (banner) via `FinalizeImageRequest.letteringUrl` so
     * both formats share one logo. Null for og and reused-logo calls.
     */
    lettering_url?: string | null;
}

/** `result` payload of a completed `asset_finalize` job (Contract 2). */
export interface FinalizeResult {
    asset_type: FinalizeAssetType;
    title: string;
    candidates: FinalizeCandidate[];
    base_plate_url: string;
    composed_prompt: string;
    /** `flux2_pro_compose` (icon/banner) or `flux2_pro_edit` (og). */
    vendor: string;
    latency_ms: number;
    cost_credits: number;
    /** Grok authoring cost (USD) — telemetry; FLUX/Recraft credits are in cost_credits. */
    grok_cost_usd?: number;
}

/** Body of `POST /asset/finalize` (camelCase; mapped to wire on send). */
export interface FinalizeImageRequest {
    jobId: string;
    /**
     * Realm scope. Realm-level finalized assets (e.g. an OG image) carry it.
     * Game-template default icon/banner (no realm) omit it — authorized on
     * global-admin (Contract 2 v0.6).
     */
    realmId?: string | null;
    /** The text-free plate to overlay the name onto, as a public URL. Provide a URL OR base64. */
    basePlateUrl?: string | null;
    /** The text-free plate as inline base64 (e.g. read via getSiteTemplateFile). */
    basePlateB64?: string | null;
    assetType: FinalizeAssetType;
    title: string;
    /** Required for `og`. */
    tagline?: string | null;
    /**
     * Brand kit `*.style.json` (v0.7). Grok authors the logo treatment
     * (icon/banner) / OG prompt from it — pass the reference template's style.
     */
    styleDescriptor?: Record<string, unknown> | null;
    /**
     * icon/banner (v0.7): reuse a pre-generated logo (a candidate's
     * `lettering_url` from a prior icon job) so banner matches icon. Provide a
     * URL OR base64. `og` rejects both.
     */
    letteringUrl?: string | null;
    letteringB64?: string | null;
    titleSeed?: number | null;
    samples?: number;
}

// ── Contract 3 — Pages (storyboard) ─────────────────────────────────────────

export type PageScene =
    | 'feature_showcase'
    | 'social_multiplayer'
    | 'promo_bonus'
    | 'gameplay_hook'
    | 'reward_payout'
    | 'custom';

export interface PageCandidate {
    url: string;
    seed: number | null;
    width: number;
    height: number;
    format: string;
    size_bytes: number;
}

export interface PageResultItem {
    index: number;
    scene: PageScene;
    cta_text: string;
    copy_source: 'operator' | 'grok';
    candidates: PageCandidate[];
}

/** `result` payload of a completed `pages` job (Contract 3). */
export interface PagesResult {
    title: string;
    pages: PageResultItem[];
    scene_source: 'operator' | 'grok';
    composed_prompts: string[];
    vendor: string;
    latency_ms: number;
    cost_credits: number;
}

export interface PagesGenerateRequest {
    jobId: string;
    /** Optional — game-template default pages carry no realm (global-admin). */
    realmId?: string | null;
    templateId: string;
    version: string;
    styleDescriptor?: Record<string, unknown> | null;
    /** Brand references — provide URLs OR base64. */
    referenceImageUrls?: string[];
    referenceImagesB64?: string[];
    /** The game's icon/banner base plates as extra references — URLs OR base64. */
    gameBaseUrls?: string[];
    gameBaseB64?: string[];
    /**
     * The finalized title wordmark (from the `iconBanner` variant) to composite
     * as the page title — URL or base64. Keeps the logo pixel-consistent with
     * the game's icon/banner. Optional.
     */
    letteringUrl?: string | null;
    letteringB64?: string | null;
    gameTemplateId: string;
    gameKind: string;
    gameMeta?: { name: string; description: string };
    title: string;
    sceneMode?: 'auto' | 'manual';
    pageCount?: number;
    copyMode?: 'auto' | 'manual';
    samples?: number;
}

/** Thrown when the service answers with its 400 error envelope. */
export class AiAssistApiError extends Error {
    constructor(
        readonly code: string,
        message?: string,
    ) {
        super(message ?? code);
        this.name = 'AiAssistApiError';
    }
}

/**
 * Thin client for the Platform Admin AI Assist Service (a separate backend from
 * the platform Admin API — it does NOT go through lib-backend). POST-only.
 * Auth: forwards the admin Bearer token, which the service validates via the
 * existing `admin/auth/me` introspection. The base URL is relative and routed
 * to the shared service instance by the reverse proxy (published) or the
 * ng-serve proxy (local).
 */
@Injectable({ providedIn: 'root' })
export class AiAssistService {
    private readonly baseUrl = environment.aiAssistBaseUrl;
    private readonly auth = inject(AuthService);

    /** Latest `/health` payload, or null if the last probe failed / none yet. */
    readonly health = signal<AiAssistHealth | null>(null);

    /** Derived traffic-light status for the topbar dot. */
    readonly status = signal<AiAssistStatus>('unknown');

    /** True while a `/health` probe is in flight (drives the popover skeleton). */
    readonly healthLoading = signal(false);

    /** The current operator's recent jobs (topbar widget). */
    readonly jobs = signal<AiAssistJob[]>([]);

    /** Count of the operator's active (pending) jobs. */
    readonly jobsActive = signal(0);

    /** False when the last `myJobs` probe failed (→ show "couldn't load"). */
    readonly jobsLoaded = signal(false);

    /** Latest vendor credit balances, or null if the last probe failed / none yet. */
    readonly vendorBalances = signal<VendorBalancesResponse | null>(null);

    /** False when the last balance probe failed (→ show "couldn't load"). */
    readonly vendorBalancesLoaded = signal(false);

    /** True while a balance probe is in flight (drives the popover skeleton). */
    readonly vendorBalancesLoading = signal(false);

    /**
     * Probe `POST /health` (no auth) and update `health` + `status`. Never
     * throws — a failed probe is reported as `down`.
     */
    async refreshHealth(): Promise<void> {
        this.healthLoading.set(true);
        try {
            const h = await this.post<AiAssistHealth>('health', {}, { auth: false });
            this.health.set(h);
            const vendorsOk =
                h.vendors_reachable.grok && h.vendors_reachable.flux && h.vendors_reachable.recraft;
            this.status.set(h.db.reachable && vendorsOk ? 'ok' : 'degraded');
        } catch {
            this.health.set(null);
            this.status.set('down');
        } finally {
            this.healthLoading.set(false);
        }
    }

    /**
     * One-off read of `POST /asset/jobs/mine` that touches **no shared signal**.
     *
     * `refreshMyJobs` clears `jobs` / `jobsActive` on any failure, which is
     * right for the dashboard widget it feeds and wrong for anything else: a
     * single network blip would blank the provisioning badge and the widget
     * together. Run discovery uses this instead, and answers `null` when the
     * probe fails so the caller can tell "no runs" from "could not ask".
     */
    async listMyJobs(opts?: {
        statuses?: AiAssistJob['status'][];
        kinds?: string[];
        limit?: number;
    }): Promise<MyJobsResponse | null> {
        try {
            return await this.post<MyJobsResponse>('asset/jobs/mine', {
                statuses: opts?.statuses ?? null,
                kinds: opts?.kinds ?? null,
                limit: opts?.limit ?? 50,
            });
        } catch {
            return null;
        }
    }

    /**
     * Probe `POST /asset/jobs/mine` (admin auth) and update `jobs` /
     * `jobsActive` / `jobsLoaded`. Never throws — a failed probe clears the
     * list and marks `jobsLoaded` false.
     */
    async refreshMyJobs(opts?: {
        statuses?: AiAssistJob['status'][];
        limit?: number;
    }): Promise<void> {
        try {
            const res = await this.post<MyJobsResponse>('asset/jobs/mine', {
                statuses: opts?.statuses ?? null,
                kinds: null,
                limit: opts?.limit ?? 50,
            });
            this.jobs.set(res.jobs);
            this.jobsActive.set(res.active);
            this.jobsLoaded.set(true);
        } catch {
            this.jobs.set([]);
            this.jobsActive.set(0);
            this.jobsLoaded.set(false);
        }
    }

    /**
     * Probe `POST /vendors/balance` (admin auth) and update `vendorBalances` /
     * `vendorBalancesLoaded`. Pass `force` to bypass the service-side cache on
     * an explicit operator refresh. Never throws — a failed probe clears the
     * snapshot and marks `vendorBalancesLoaded` false.
     */
    async refreshVendorBalances(force = false): Promise<void> {
        this.vendorBalancesLoading.set(true);
        try {
            const res = await this.post<VendorBalancesResponse>('vendors/balance', { force });
            this.vendorBalances.set(res);
            this.vendorBalancesLoaded.set(true);
        } catch {
            this.vendorBalances.set(null);
            this.vendorBalancesLoaded.set(false);
        } finally {
            this.vendorBalancesLoading.set(false);
        }
    }

    /** The latest balance snapshot for a single vendor, or null if unseen. */
    balanceFor(vendor: string): VendorBalance | null {
        return this.vendorBalances()?.vendors.find((v) => v.vendor === vendor) ?? null;
    }

    /**
     * Vendors currently `critical` (out of credits) from the latest snapshot.
     * The service is the authoritative gate; this is a UX pre-check so the
     * operator sees *why* a generation is disabled. Fails open: returns `[]`
     * when balances are unknown, matching the server's behaviour.
     */
    blockedVendors(): string[] {
        return (
            this.vendorBalances()
                ?.vendors.filter((v) => v.status === 'critical')
                .map((v) => v.vendor) ?? []
        );
    }

    /**
     * Rewrite / improve operator-supplied text via Grok (`POST /text/refine`).
     * Sync (< ~8 s service-side). Generates the per-call `request_id` itself.
     * Throws `AiAssistApiError` on the service's 400 error envelope.
     */
    async refine(req: RefineRequest): Promise<RefineResponse> {
        return this.post<RefineResponse>('text/refine', {
            request_id: crypto.randomUUID(),
            text: req.text,
            instruction: req.instruction,
            max_length: req.maxLength ?? null,
        });
    }

    /**
     * Draft SEO meta-description options via Grok (`POST /text/suggest-branding`).
     * Sync. Generates the per-call `request_id` itself. Throws `AiAssistApiError`
     * on the service's 400 error envelope.
     */
    async suggestBranding(req: SuggestBrandingRequest): Promise<SuggestBrandingResponse> {
        return this.post<SuggestBrandingResponse>('text/suggest-branding', {
            request_id: crypto.randomUUID(),
            brand_name: req.brandName,
            domain: req.domain,
            vertical: req.vertical ?? null,
            style: req.style ?? null,
            context: {
                display_name: req.context?.displayName ?? null,
                name: req.context?.name ?? null,
            },
            operator_note: req.operatorNote ?? null,
        });
    }

    /**
     * Kick off text-free base-art generation (`POST /asset/base/generate`).
     * Async: returns 202 + `job_id`; poll the result with `awaitJob(job_id)`.
     */
    async generateBaseArt(req: BaseArtGenerateRequest): Promise<JobAccepted> {
        return this.post<JobAccepted>('asset/base/generate', {
            request_id: crypto.randomUUID(),
            job_id: req.jobId,
            realm_id: req.realmId ?? null,
            template_id: req.templateId,
            version: req.version,
            style_descriptor: req.styleDescriptor ?? {},
            reference_image_urls: req.referenceImageUrls ?? [],
            reference_images_b64: req.referenceImagesB64 ?? [],
            asset_type: req.assetType,
            game_template_id: req.gameTemplateId ?? null,
            game_kind: req.gameKind ?? null,
            game_meta: req.gameMeta ?? null,
            mode: req.mode ?? 'auto',
            prompt: req.prompt ?? null,
            operator_note: req.operatorNote ?? null,
            samples: req.samples ?? 4,
        });
    }

    /**
     * Kick off name-overlay finalization (`POST /asset/finalize`) — e.g. the
     * realm OG image from a base plate. Async: returns 202 + `job_id`.
     */
    async finalizeImage(req: FinalizeImageRequest): Promise<JobAccepted> {
        return this.post<JobAccepted>('asset/finalize', {
            request_id: crypto.randomUUID(),
            job_id: req.jobId,
            realm_id: req.realmId ?? null,
            base_plate_url: req.basePlateUrl ?? null,
            base_plate_b64: req.basePlateB64 ?? null,
            asset_type: req.assetType,
            title: req.title,
            tagline: req.tagline ?? null,
            style_descriptor: req.styleDescriptor ?? null,
            lettering_url: req.letteringUrl ?? null,
            lettering_b64: req.letteringB64 ?? null,
            title_seed: req.titleSeed ?? null,
            samples: req.samples ?? 4,
        });
    }

    /**
     * Generate storyboard pages (Contract 3, `POST /asset/pages/generate`).
     * Async: returns 202 + `job_id`; poll with `awaitJob` and read `PagesResult`.
     */
    async generatePages(req: PagesGenerateRequest): Promise<JobAccepted> {
        return this.post<JobAccepted>('asset/pages/generate', {
            request_id: crypto.randomUUID(),
            job_id: req.jobId,
            realm_id: req.realmId ?? null,
            template_id: req.templateId,
            version: req.version,
            style_descriptor: req.styleDescriptor ?? {},
            reference_image_urls: req.referenceImageUrls ?? [],
            reference_images_b64: req.referenceImagesB64 ?? [],
            game_base_urls: req.gameBaseUrls ?? [],
            game_base_b64: req.gameBaseB64 ?? [],
            lettering_url: req.letteringUrl ?? null,
            lettering_b64: req.letteringB64 ?? null,
            game_template_id: req.gameTemplateId,
            game_kind: req.gameKind,
            game_meta: req.gameMeta ?? null,
            title: req.title,
            scene_mode: req.sceneMode ?? 'auto',
            page_count: req.pageCount ?? 3,
            copy_mode: req.copyMode ?? 'auto',
            samples: req.samples ?? 2,
        });
    }

    /**
     * Cost + wall-clock estimate for a planned site kit (`POST /site/kit/estimate`).
     *
     * Cheap and synchronous: no vendor call, no job row, no draft. The service
     * owns the cost model deliberately — a price table duplicated in Angular
     * goes stale silently, and once the ledger has data the constants are
     * replaced by medians without the panel changing.
     */
    async estimateSiteKit(req: SiteKitEstimateRequest): Promise<SiteKitEstimate> {
        return this.post<SiteKitEstimate>('site/kit/estimate', {
            request_id: crypto.randomUUID(),
            realm_id: req.realm_id,
            branding: req.branding,
            games: req.games,
        });
    }

    /**
     * Stage input images straight in the service bucket
     * (`POST /asset/inbound/presign`). The caller PUTs the bytes to
     * `upload_url` — S3 directly, not through the gateway — and sends `url` in
     * place of base64, which is what keeps a whole brand kit out of one
     * request. **Keys are composed service-side**: a client-chosen key would be
     * a licence to overwrite any object in the bucket.
     */
    async presignInbound(
        jobId: string,
        files: { name: string; content_type: InboundContentType }[],
    ): Promise<InboundPresignResponse> {
        return this.post<InboundPresignResponse>('asset/inbound/presign', {
            request_id: crypto.randomUUID(),
            job_id: jobId,
            files,
        });
    }

    /** Open a kit draft (`POST /site/kit/create`). No vendor work, cheap to retry. */
    async createSiteKit(req: SiteKitCreateRequest): Promise<SiteKitCreateResponse> {
        return this.post<SiteKitCreateResponse>('site/kit/create', {
            request_id: crypto.randomUUID(),
            ...req,
        });
    }

    /**
     * Add one game to the draft (`POST /site/kit/item`). Re-posting the same
     * `item_key` before `start` replaces the stored item, which is how a wrong
     * plate is corrected — and what makes a retried submission idempotent.
     */
    async addSiteKitItem(req: SiteKitItemRequest): Promise<SiteKitItemResponse> {
        return this.post<SiteKitItemResponse>('site/kit/item', {
            request_id: crypto.randomUUID(),
            ...req,
        });
    }

    /**
     * Dispatch the kit (`POST /site/kit/start`). Refused when the draft is
     * short of `expected_item_count`. On an already-run job this is the
     * **resume**: it re-runs only what the checkpoint does not record as
     * finished, which is exactly what "Retry failed" needs.
     */
    async startSiteKit(jobId: string): Promise<SiteKitStartResponse> {
        return this.post<SiteKitStartResponse>('site/kit/start', {
            request_id: crypto.randomUUID(),
            job_id: jobId,
        });
    }

    /**
     * Ask a running job to stop (`POST /asset/jobs/{job_id}/cancel`).
     * Cooperative: in-flight billed work still lands and is checkpointed, so a
     * cancelled kit resumes rather than restarts.
     */
    async cancelJob(jobId: string): Promise<JobStatusResponse> {
        return this.post<JobStatusResponse>(`asset/jobs/${jobId}/cancel`, {
            request_id: crypto.randomUUID(),
        });
    }

    /** Single poll of an async job status (`POST /asset/jobs/{job_id}/status`). */
    async pollJob(jobId: string): Promise<JobStatusResponse> {
        return this.post<JobStatusResponse>(`asset/jobs/${jobId}/status`, {
            request_id: crypto.randomUUID(),
        });
    }

    /**
     * Poll an async job until it terminates. Resolves with the completed
     * `JobStatusResponse` (read `result`), or throws `AiAssistApiError` on a
     * failed / cancelled job, a timeout (`vendor_timeout`), or abort.
     */
    async awaitJob(
        jobId: string,
        opts: {
            intervalMs?: number;
            timeoutMs?: number;
            signal?: AbortSignal;
        } = {},
    ): Promise<JobStatusResponse> {
        const intervalMs = opts.intervalMs ?? 2500;
        const timeoutMs = opts.timeoutMs ?? 180_000;
        const deadline = Date.now() + timeoutMs;

        for (;;) {
            if (opts.signal?.aborted) {
                throw new AiAssistApiError('internal_error', 'Polling aborted');
            }
            const res = await this.pollJob(jobId);
            if (res.status === 'completed') return res;
            if (res.status === 'failed') {
                throw new AiAssistApiError(res.error?.code ?? 'internal_error', res.error?.message);
            }
            if (res.status === 'cancelled') {
                throw new AiAssistApiError('internal_error', 'The job was cancelled.');
            }
            if (Date.now() >= deadline) {
                throw new AiAssistApiError(
                    'vendor_timeout',
                    'Timed out waiting for the image job to finish.',
                );
            }
            await new Promise((resolve) => setTimeout(resolve, intervalMs));
        }
    }

    /**
     * POST a JSON body to a service endpoint. Attaches the admin Bearer token by
     * default (every endpoint except `/health` requires it). Throws
     * `AiAssistApiError` on the service's 400 error envelope.
     *
     * The token comes from `AuthService.getFreshAuthToken()`, which refreshes it
     * through the lib session when it is close to expiry — required here because
     * these requests bypass `SessionAdminApiClient` and would otherwise run on a
     * stale stored token during long job-polling stretches. A rejection with the
     * service's `unauthorized` code still gets one forced-refresh retry to cover
     * expiry races around the refresh threshold.
     */
    async post<T>(path: string, body: unknown, opts: { auth?: boolean } = {}): Promise<T> {
        if (opts.auth === false) {
            return this.doPost<T>(path, body, null);
        }

        const token = await this.auth.getFreshAuthToken();
        if (!token) {
            throw new AiAssistApiError('unauthorized', 'No admin session token');
        }

        try {
            return await this.doPost<T>(path, body, token);
        } catch (e) {
            if (!(e instanceof AiAssistApiError) || e.code !== 'unauthorized') {
                throw e;
            }
            const fresh = await this.auth.getFreshAuthToken({
                forceRefresh: true,
            });
            // No retry when the refresh produced nothing new — the session is
            // gone (rethrow → caller surfaces auth error; login redirect has
            // already happened) or the service rejects a token the session
            // still considers valid (retrying would loop on the same error).
            if (!fresh || fresh === token) throw e;
            return this.doPost<T>(path, body, fresh);
        }
    }

    private async doPost<T>(path: string, body: unknown, token: string | null): Promise<T> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${this.baseUrl}${path}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            cache: 'no-store',
        });

        const data: unknown = await response.json().catch(() => null);

        if (!response.ok) {
            const err = (data ?? {}) as AiAssistErrorBody;
            // A gateway rejection never reaches us in the service's envelope —
            // nginx answers with an HTML page, so `json()` yields null and the
            // error would read as a generic `internal_error` when in fact one
            // config line is missing. Name it, so the operator is not sent
            // hunting through service logs for a proxy setting.
            if (!err.code) {
                const gateway = gatewayFault(response.status);
                if (gateway) throw new AiAssistApiError(gateway.code, gateway.message);
            }
            throw new AiAssistApiError(err.code ?? 'internal_error', err.message);
        }

        return data as T;
    }
}

/**
 * Rejections that come from the reverse proxy rather than from the service.
 * Both are configuration on `/admin-ai-assist/`, and both have defaults that
 * are wrong for this service: `client_max_body_size` (1 MB) and
 * `proxy_read_timeout` (60 s).
 */
function gatewayFault(status: number): { code: string; message: string } | null {
    if (status === 413) {
        return {
            code: 'gateway_request_too_large',
            message:
                'The gateway rejected the request as too large. Staged uploads should keep every request small — if this is the base64 path, the proxy needs client_max_body_size raised.',
        };
    }
    if (status === 502 || status === 503 || status === 504) {
        return {
            code: 'gateway_timeout',
            message:
                'The gateway closed the connection before the service answered. Long calls need proxy_read_timeout 300s on /admin-ai-assist/.',
        };
    }
    return null;
}
